import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import Loading from "../../../components/UI/loading";
import CorrectStudentModal from "../../../containers/admin/essay-correction/correct-student-modal";
import {
  getAllExamsRequest,
  getAllExamsSuperAdminRequest,
  getEssayAnswersRequest,
} from "../../../modules/admin-modules/exams/Actions";

const colourStyles = {
  control: (styles) => ({
    ...styles,
    width: "100%",
    background: "#f5f5f5",
    color: "#690000",
    borderRadius: "16px",
  }),
  option: (styles) => ({ ...styles, color: "#690000" }),
  menu: (styles) => ({ ...styles, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menuList: (base) => ({ ...base, maxHeight: "240px" }),
};

const statusOptions = [
  { value: "all", label: "الكل" },
  { value: "pending", label: "قيد الانتظار" },
  { value: "corrected", label: "تم التصحيح" },
];

const isAnswerMarked = (answer) =>
  answer?.mark != null && answer?.mark !== "";

const EssayCorrection = () => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.userData);
  const exams = useSelector((state) => state.exams.exams);
  const isLoading = useSelector((state) => state.exams.isLoading);
  const essayAnswers = useSelector((state) => state.exams.essayAnswers);
  const essayAnswersLoading = useSelector(
    (state) => state.exams.essayAnswersLoading
  );

  const [examId, setExamId] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    if (userData?.role?.length > 0) {
      if (userData?.role[0]?.name === "Teacher") {
        dispatch(getAllExamsRequest());
      } else {
        dispatch(getAllExamsSuperAdminRequest());
      }
    }
  }, [userData, dispatch]);

  const examOptions = useMemo(
    () =>
      (exams || []).map((exam) => {
        const essayCount = exam.essay_count ?? 0;
        return {
          value: exam.id,
          label: `${exam.name} (${essayCount})`,
        };
      }),
    [exams]
  );

  const students = useMemo(() => {
    const grouped = {};
    (essayAnswers || []).forEach((item) => {
      const studentId = item.student_id || item.student?.id;
      if (!studentId) return;
      if (!grouped[studentId]) {
        grouped[studentId] = {
          student_id: studentId,
          name: item.student?.name || "-",
          user_name: item.student?.user_name || "-",
          phone: item.student?.phone || "-",
          student_exam: item.student_exam,
          answers: [],
        };
      }
      grouped[studentId].answers.push(item);
    });
    return Object.values(grouped).map((student) => {
      const corrected = student.answers.every(isAnswerMarked);
      return {
        ...student,
        status: corrected ? "corrected" : "pending",
        statusLabel: corrected ? "تم التصحيح" : "قيد الانتظار",
      };
    });
  }, [essayAnswers]);

  const filteredStudents = useMemo(() => {
    if (statusFilter === "all") return students;
    return students.filter((student) => student.status === statusFilter);
  }, [students, statusFilter]);

  const handleExamChange = (option) => {
    const id = option?.value || "";
    setExamId(id);
    setSelectedStudent(null);
    setStatusFilter("all");
    if (id) dispatch(getEssayAnswersRequest(id));
  };

  return (
    <Layout>
      <VStack p="20px" spacing="30px" align="stretch" dir="rtl">
        <Flex alignItems="center" w="100%" justifyContent="space-between">
          <Text fontSize="24px" fontWeight="600" color="main_1">
            تصحيح الاسئلة المقالية
          </Text>
          <HStack>
            <LogoutMenu />
          </HStack>
        </Flex>

        <Flex gap={4} flexWrap="wrap">
          <Box minW="280px" maxW="500px" flex="1">
            <Select
              options={examOptions}
              value={examOptions.find((o) => String(o.value) === String(examId)) || null}
              onChange={handleExamChange}
              styles={colourStyles}
              placeholder="اختر الامتحان"
              isLoading={isLoading}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
            />
          </Box>
          <Box minW="220px" maxW="280px">
            <Select
              options={statusOptions}
              value={statusOptions.find((o) => o.value === statusFilter) || statusOptions[0]}
              onChange={(option) => setStatusFilter(option?.value || "all")}
              styles={colourStyles}
              placeholder="الحالة"
              isDisabled={!examId}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
            />
          </Box>
        </Flex>

        {essayAnswersLoading ? (
          <Loading />
        ) : examId && students.length === 0 ? (
          <Text color="gray.600">لا توجد إجابات مقالية لهذا الامتحان</Text>
        ) : examId && filteredStudents.length === 0 ? (
          <Text color="gray.600">لا يوجد طلاب بهذه الحالة</Text>
        ) : filteredStudents.length > 0 ? (
          <TableContainer w="100%">
            <Table>
              <Thead bg="main_1_light" color="white">
                <Tr>
                  <Th>#</Th>
                  <Th>اسم الطالب</Th>
                  <Th>كود الطالب</Th>
                  <Th>عدد الأسئلة المقالية</Th>
                  <Th>الحالة</Th>
                  <Th>إجراء</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredStudents.map((student, idx) => (
                  <Tr key={student.student_id}>
                    <Td>{idx + 1}</Td>
                    <Td>{student.name}</Td>
                    <Td>{student.user_name}</Td>
                    <Td>{student.answers.length}</Td>
                    <Td>
                      <Badge
                        colorScheme={student.status === "corrected" ? "green" : "orange"}
                        borderRadius="8px"
                        px={2}
                        py={1}
                      >
                        {student.statusLabel}
                      </Badge>
                    </Td>
                    <Td>
                      <Button
                        bg="main_1"
                        color="white"
                        size="sm"
                        onClick={() => setSelectedStudent(student)}
                      >
                        تصحيح إجابات الطالب
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        ) : null}
      </VStack>

      <CorrectStudentModal
        isOpen={Boolean(selectedStudent)}
        onClose={() => setSelectedStudent(null)}
        studentGroup={selectedStudent}
        examId={examId}
      />
    </Layout>
  );
};

export default EssayCorrection;
